/**
 * Protected Route Component
 *
 * Wraps routes that require authentication
 */

import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/application/auth'
import { APP_ROUTES } from '@/config/constants'
import { Loader2 } from 'lucide-react'

interface ProtectedRouteProps {
  children: React.ReactNode
  allowedRoles?: string[]
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, mustChangePassword } = useAuth()
  const location = useLocation()

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className="min-h-screen alto-de-pantalla flex items-center justify-center bg-[var(--background)]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
          <p className="text-[var(--foreground-muted)]">Cargando...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={APP_ROUTES.LOGIN} state={{ from: location }} replace />
  }

  // Force password change before accessing any protected route
  if (mustChangePassword && location.pathname !== APP_ROUTES.CHANGE_PASSWORD) {
    return <Navigate to={APP_ROUTES.CHANGE_PASSWORD} replace />
  }

  // Check role authorization
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to={APP_ROUTES.DASHBOARD} replace />
  }

  return <>{children}</>
}

/**
 * Public Route Component
 *
 * Wraps routes that should only be accessible when NOT authenticated
 */
export function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, mustChangePassword } = useAuth()

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className="min-h-screen alto-de-pantalla flex items-center justify-center bg-[var(--background)]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
          <p className="text-[var(--foreground-muted)]">Cargando...</p>
        </div>
      </div>
    )
  }

  if (isAuthenticated) {
    // If user must change their password, send them there first
    if (mustChangePassword) {
      return <Navigate to={APP_ROUTES.CHANGE_PASSWORD} replace />
    }
    return <Navigate to={APP_ROUTES.DASHBOARD} replace />
  }

  return <>{children}</>
}
